"use client";

import { useMemo } from "react";
import { api } from "@/api";
import useSWR, { SWRResponse } from "swr";
import type { SWRConfiguration } from "swr";
import { useLocation } from "react-router-dom";

const useApi = <T = any, K = any>(
  url: string,
  key?: string,
  config: SWRConfiguration = { shouldRetryOnError: false }
): SWRResponse<T, K> => {
  const location = useLocation();

  // Create a cache key that includes query parameters from the current location
  // This ensures that when query parameters change, we get fresh data
  const cacheKey = useMemo(() => {
    if (key) return key;

    // If the URL already contains query parameters, use it as is
    if (url.includes("?")) return url;

    // Only include query parameters that are likely to affect the data
    // UI-only parameters like 'tab' should not affect the cache key
    const currentSearch = location.search;
    if (currentSearch) {
      const urlParams = new URLSearchParams(currentSearch);
      const relevantParams = new URLSearchParams();

      // Only include parameters that typically affect data fetching
      // Exclude UI-only parameters like 'tab', 'introView', etc.
      const relevantParamNames = [
        "skip",
        "limit",
        "page",
        "sort",
        "filter",
        "search",
        "date",
        "status",
      ];
      relevantParamNames.forEach((param) => {
        if (urlParams.has(param)) {
          relevantParams.append(param, urlParams.get(param)!);
        }
      });

      const relevantSearch = relevantParams.toString();
      if (relevantSearch) {
        return `${url}?${relevantSearch}`;
      }
    }

    return url;
  }, [key, url, location.search]);

  return useSWR(
    cacheKey,
    async () => {
      const res = await api.get(url);
      return res.data;
    },
    {
      ...config,
      // Ensure data is revalidated when the URL changes
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      // Add deduplication interval to prevent duplicate requests
      dedupingInterval: 2000,
    }
  );
};
export default useApi;
